import React, { useState } from 'react';
import { InfoTooltip } from '../../primitives/InfoTooltip';
import {
  StepPage,
  TextInput,
  ChoiceCard,
  WorldModePill,
  FilterChip,
  TemplateTile,
  ExistingCampaignTile,
  ThemeButton,
} from './widgets';
import {
  ALL_HOMEBREW_KEYS,
  FEATURES,
  MAX_TAGLINE_LENGTH,
  TEMPLATE_DEFAULTS,
} from './constants';
import { MOCK_TEMPLATES, MOCK_EXISTING_CAMPAIGNS } from './mockData';

// Step 1: Begin — name, tagline, origin selection, world / template /
// existing-campaign sub-flow, plus the theme picker entry button.
// Ported from prototypes/campaign-creation-prototype.jsx (lines 809-1268).
// Live dashboard mounts pass real worlds/campaigns; story/prototype mounts
// can still rely on fixture fallbacks.
export const BeginStep = ({
  form,
  update,
  theme,
  onOpenThemePicker,
  worlds = [],
  worldsLoading = false,
  existingCampaigns = MOCK_EXISTING_CAMPAIGNS,
  sourceImportEnabled = true,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [worldFilter, setWorldFilter] = useState('all');

  // Build the filter categories list. Each category has an id, name, and
  // campaign count. Includes a synthetic "isolated" category for campaigns
  // without a worldId.
  const categories = (() => {
    const list = [];
    worlds.forEach((w) => {
      const count = existingCampaigns.filter((cp) => cp.worldId === w.id).length;
      if (count > 0) list.push({ id: w.id, name: w.name, count });
    });
    const isolatedCount = existingCampaigns.filter((cp) => !cp.worldId).length;
    if (isolatedCount > 0) list.push({ id: 'isolated', name: 'No linked world', count: isolatedCount });
    list.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    return list;
  })();

  const PILL_LIMIT = 3;
  const showDropdown = categories.length > 4;
  const pillCategories = showDropdown ? categories.slice(0, PILL_LIMIT) : categories;
  const dropdownCategories = showDropdown ? categories.slice(PILL_LIMIT) : [];
  const activeIsInDropdown = dropdownCategories.some((cat) => cat.id === worldFilter);

  const filteredExisting = worldFilter === 'all'
    ? existingCampaigns
    : worldFilter === 'isolated'
      ? existingCampaigns.filter((cp) => !cp.worldId)
      : existingCampaigns.filter((cp) => cp.worldId === worldFilter);
  const selectedExisting = existingCampaigns.find((cp) => cp.id === form.existingId);
  const canUseExistingOrigin = sourceImportEnabled && existingCampaigns.length > 0;

  const resetHomebrew = () => Object.fromEntries(ALL_HOMEBREW_KEYS.map((k) => [k, false]));
  const resetFeatures = () => Object.fromEntries(FEATURES.map((f) => [f.key, true]));
  const sourceHomebrewDefaults = (homebrew) => {
    if (homebrew && typeof homebrew === 'object' && Object.keys(homebrew).length > 0) {
      return { ...resetHomebrew(), ...homebrew };
    }
    return Object.fromEntries(ALL_HOMEBREW_KEYS.map((k) => [k, true]));
  };

  return (
    <StepPage theme={theme}>
      {/* ===== Name + tagline ===== */}
      <div style={{ marginBottom: 32 }}>
        <TextInput
          theme={theme}
          value={form.name}
          onChange={(v) => update({ name: v })}
          placeholder="New Campaign Name"
          big
          autoFocus
        />
        <div style={{ marginTop: 14 }}>
          <TextInput
            theme={theme}
            value={form.tagline}
            onChange={(v) => update({ tagline: v })}
            placeholder="Tagline (optional)"
            maxLength={MAX_TAGLINE_LENGTH}
          />
        </div>
      </div>

      {/* ===== Origin ===== */}
      <div style={{ marginBottom: 24 }}>
        <div style={{
          fontSize: '0.92rem',
          color: c.textSecondary,
          marginBottom: 10,
          fontFamily: t.fontFamily,
        }}>How does it start?</div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
          gap: 10,
        }}>
          <ChoiceCard
            theme={theme}
            active={form.origin === 'scratch'}
            onClick={() => {
              if (form.origin === 'scratch') return;
              update({ origin: 'scratch', homebrew: resetHomebrew(), features: resetFeatures() });
            }}
            icon="◯"
            title="From scratch"
            desc="An empty campaign. Build it as you go."
          />
          <ChoiceCard
            theme={theme}
            active={form.origin === 'template'}
            onClick={() => {
              if (form.origin === 'template') return;
              update({ origin: 'template', homebrew: resetHomebrew(), features: resetFeatures() });
            }}
            icon="✦"
            title="From template"
            desc={<>A starter shell with rules baked in.<br />&nbsp;</>}
          />
          {canUseExistingOrigin && (
          <ChoiceCard
            theme={theme}
            active={form.origin === 'existing'}
            onClick={() => {
              if (form.origin === 'existing') return;
              update({ origin: 'existing', homebrew: resetHomebrew(), features: resetFeatures() });
            }}
            icon="⥁"
            title="From an existing campaign"
            desc="Use a campaign you manage as a setup source."
          />
          )}
        </div>

        {/* ----- Origin-specific reveal pane ----- */}
        {form.origin === 'scratch' && (
          <div style={{ marginTop: 18 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontFamily: t.fontFamily,
                fontSize: '0.85rem',
                color: c.textSecondary,
                fontWeight: 500,
              }}>
                <InfoTooltip
                  theme={theme}
                  text="Group this campaign under a world. Campaign NPC state, notes, and edits stay campaign-local unless later applied through explicit world-canon tools."
                />
                World
              </div>
              <WorldModePill
                theme={theme}
                active={form.worldMode === 'isolated'}
                onClick={() => update({ worldMode: 'isolated', worldId: '', newWorldName: '' })}
                label="No linked world"
              />
              {worlds.length > 0 && (
                <WorldModePill
                  theme={theme}
                  active={form.worldMode === 'existing'}
                  onClick={() => update({ worldMode: 'existing', worldId: form.worldId || worlds[0]?.id || '', newWorldName: '' })}
                  label="Existing"
                />
              )}
              <WorldModePill
                theme={theme}
                active={form.worldMode === 'new'}
                onClick={() => update({ worldMode: 'new', worldId: '' })}
                label="New"
              />

              {form.worldMode === 'existing' && (
                <select
                  value={form.worldId}
                  onChange={(ev) => update({ worldId: ev.target.value })}
                  style={{
                    padding: '5px 28px 5px 10px',
                    background: c.bgInput,
                    border: `1px solid ${c.accent}`,
                    borderRadius: 6,
                    color: c.textPrimary,
                    fontSize: '0.84rem',
                    fontFamily: t.fontFamily,
                    outline: 'none',
                    cursor: 'pointer',
                    appearance: 'none',
                    minWidth: 180,
                  }}
                >
                  {worlds.map((w) => (
                    <option key={w.id} value={w.id} style={{ background: c.bgCardSolid }}>
                      {w.name}{(w.loreCount || w.atlasPlaceCount)
                        ? ` (${w.loreCount || 0} lore, ${w.atlasPlaceCount || 0} places)`
                        : ''}
                    </option>
                  ))}
                </select>
              )}
              {worldsLoading && form.worldMode !== 'new' && (
                <span style={{
                  color: c.textMuted,
                  fontFamily: t.fontFamily,
                  fontSize: '0.78rem',
                  fontStyle: 'italic',
                }}>Loading worlds...</span>
              )}
              {form.worldMode === 'new' && (
                <input
                  type="text"
                  value={form.newWorldName}
                  onChange={(ev) => update({ newWorldName: ev.target.value })}
                  autoFocus
                  placeholder="Name your new world…"
                  style={{
                    padding: '5px 10px',
                    background: c.bgInput,
                    border: `1px solid ${c.accent}`,
                    borderRadius: 6,
                    color: c.textPrimary,
                    fontSize: '0.84rem',
                    fontFamily: t.fontFamily,
                    outline: 'none',
                    minWidth: 200,
                    boxSizing: 'border-box',
                  }}
                />
              )}
            </div>

            {form.worldMode === 'isolated' && (
              <div style={{
                fontSize: '0.78rem',
                color: c.textMuted,
                fontStyle: 'italic',
                fontFamily: t.fontFamily,
                marginTop: 6,
                textAlign: 'center',
              }}>
                No linked world. This campaign's content stays campaign-local.
              </div>
            )}
          </div>
        )}

        {form.origin === 'template' && (
          <div style={{ marginTop: 18 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 8,
            }}>
              {MOCK_TEMPLATES.map((tpl) => (
                <TemplateTile
                  key={tpl.id}
                  tpl={tpl}
                  active={form.templateId === tpl.id}
                  onClick={() => {
                    const baseHomebrew = resetHomebrew();
                    const merged = { ...baseHomebrew, ...(TEMPLATE_DEFAULTS[tpl.id] || {}) };
                    update({ templateId: tpl.id, homebrew: merged });
                  }}
                  theme={theme}
                />
              ))}
            </div>
          </div>
        )}

        {canUseExistingOrigin && form.origin === 'existing' && (
          <div style={{ marginTop: 18 }}>
            {form.existingId && selectedExisting ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                marginBottom: 4,
              }}>
                <ExistingCampaignTile
                  camp={selectedExisting}
                  active
                  onClick={() => {}}
                  theme={theme}
                />
                <button
                  type="button"
                  onClick={() => update({ existingId: '', homebrew: resetHomebrew(), features: resetFeatures() })}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 6px',
                    color: c.accentLight,
                    cursor: 'pointer',
                    fontFamily: t.fontFamily,
                    fontSize: '0.82rem',
                    textDecoration: 'underline',
                    textUnderlineOffset: 3,
                  }}
                >Change</button>
              </div>
            ) : (
              <>
                {categories.length > 1 && (
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: 6,
                    alignItems: 'center',
                    marginBottom: 10,
                  }}>
                    <FilterChip
                      theme={theme}
                      active={worldFilter === 'all'}
                      onClick={() => setWorldFilter('all')}
                      label={`All (${existingCampaigns.length})`}
                    />
                    {pillCategories.map((cat) => (
                      <FilterChip
                        key={cat.id}
                        theme={theme}
                        active={worldFilter === cat.id}
                        onClick={() => setWorldFilter(cat.id)}
                        label={`${cat.name} (${cat.count})`}
                      />
                    ))}
                    {dropdownCategories.length > 0 && (
                      <select
                        value={activeIsInDropdown ? worldFilter : '__more__'}
                        onChange={(ev) => {
                          if (ev.target.value !== '__more__') {
                            setWorldFilter(ev.target.value);
                          }
                        }}
                        style={{
                          padding: '4px 24px 4px 12px',
                          background: activeIsInDropdown ? `${c.accent}1a` : 'transparent',
                          border: `1px solid ${activeIsInDropdown ? c.accent : c.borderLight}`,
                          borderRadius: 999,
                          color: activeIsInDropdown ? c.accentLight : c.textSecondary,
                          fontSize: '0.78rem',
                          fontFamily: t.fontFamily,
                          fontWeight: 500,
                          outline: 'none',
                          cursor: 'pointer',
                          appearance: 'none',
                          transition: 'all 120ms ease',
                        }}
                      >
                        {!activeIsInDropdown && (
                          <option value="__more__" style={{ background: c.bgCardSolid }}>More…</option>
                        )}
                        {dropdownCategories.map((cat) => (
                          <option key={cat.id} value={cat.id} style={{ background: c.bgCardSolid }}>{cat.name} ({cat.count})</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
                {filteredExisting.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {filteredExisting.map((camp) => (
                      <ExistingCampaignTile
                        key={camp.id}
                        camp={camp}
                        active={form.existingId === camp.id}
                        onClick={() => {
                          const sourceEnabledFeatures = camp.enabledFeatures || {};
                          update({
                            existingId: camp.id,
                            homebrew: sourceHomebrewDefaults(camp.homebrew),
                            features: { ...resetFeatures(), ...sourceEnabledFeatures },
                          });
                        }}
                        theme={theme}
                      />
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '12px 14px',
                    color: c.textMuted,
                    fontFamily: t.fontFamily,
                    fontSize: '0.85rem',
                    fontStyle: 'italic',
                    border: `1px dashed ${c.borderLight}`,
                    borderRadius: 6,
                    textAlign: 'center',
                  }}>No campaigns to show.</div>
                )}
              </>
            )}

            {form.existingId && (
              <div style={{ marginTop: 18 }}>
                <div style={{
                  padding: '10px 12px',
                  border: `1px solid ${c.borderLight}`,
                  borderRadius: 6,
                  background: c.bgHover,
                  fontFamily: t.fontFamily,
                  fontSize: '0.82rem',
                  color: c.textSecondary,
                  textAlign: 'center',
                  maxWidth: 480, marginLeft: 'auto', marginRight: 'auto',
                }}>
                  Setup content will be imported after server validation. Campaign play state stays local.
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== Theme picker entry ===== */}
      <div style={{ textAlign: 'center' }}>
        <ThemeButton theme={theme} form={form} onClick={onOpenThemePicker} />
      </div>
    </StepPage>
  );
};
